// src/pages/masters/ScriptMasterPage.js
import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Select, Table, Badge, PageHeader } from '../../components/ui';
import { Plus, Search } from 'lucide-react';

const initialForm = { exchange_id: '', script_code: '', script_name: '', isin: '', sector: '', industry: '', face_value: 10, market_cap_category: 'Large Cap' };

export default function ScriptMasterPage() {
  const [scripts, setScripts] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    api.get('/masters/exchanges').then(r => setExchanges(r.data));
    loadScripts();
  }, []);

  const loadScripts = (q = '') => {
    api.get('/masters/scripts', { params: { search: q, limit: 100 } }).then(r => setScripts(r.data));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) {
        await api.put(`/masters/scripts/${editId}`, form);
        toast.success('Script updated!');
      } else {
        await api.post('/masters/scripts', form);
        toast.success('Script added!');
      }
      setForm(initialForm); setShowForm(false); setEditId(null);
      loadScripts();
    } catch (err) { toast.error(err.response?.data?.error || 'Error saving script'); }
    finally { setLoading(false); }
  };

  const startEdit = (s) => {
    setForm({ exchange_id: s.exchange_id, script_code: s.script_code, script_name: s.script_name, isin: s.isin || '', sector: s.sector || '', industry: s.industry || '', face_value: s.face_value, market_cap_category: s.market_cap_category || 'Large Cap' });
    setEditId(s.id); setShowForm(true);
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader
        title="Script Master"
        subtitle="Manage BSE/NSE scripts & symbols"
        action={<Btn onClick={() => { setShowForm(!showForm); setEditId(null); setForm(initialForm); }}><Plus size={16} /> Add Script</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 24 }}>
          <CardHeader title={editId ? 'Edit Script' : 'Add New Script'} />
          <form onSubmit={handleSubmit} style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
              <Select label="Exchange *" value={form.exchange_id} onChange={set('exchange_id')}>
                <option value="">Select Exchange</option>
                {exchanges.map(e => <option key={e.id} value={e.id}>{e.exchange_code} - {e.exchange_name}</option>)}
              </Select>
              <Input label="Script Code *" value={form.script_code} onChange={set('script_code')} placeholder="e.g. RELIANCE" disabled={!!editId} />
              <Input label="Script Name *" value={form.script_name} onChange={set('script_name')} placeholder="e.g. Reliance Industries Ltd" />
              <Input label="ISIN" value={form.isin} onChange={set('isin')} placeholder="e.g. INE002A01018" />
              <Input label="Sector" value={form.sector} onChange={set('sector')} placeholder="e.g. Energy" />
              <Input label="Industry" value={form.industry} onChange={set('industry')} placeholder="e.g. Oil & Gas" />
              <Input label="Face Value (₹)" type="number" value={form.face_value} onChange={set('face_value')} />
              <Select label="Market Cap" value={form.market_cap_category} onChange={set('market_cap_category')}>
                {['Large Cap', 'Mid Cap', 'Small Cap', 'Micro Cap'].map(m => <option key={m}>{m}</option>)}
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Btn type="submit" variant="primary" disabled={loading}>{loading ? 'Saving...' : editId ? 'Update' : 'Add Script'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader
          title="All Scripts"
          subtitle={`${scripts.length} scripts`}
          action={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  style={{ padding: '7px 12px 7px 30px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none' }}
                  placeholder="Search scripts..." value={search}
                  onChange={e => { setSearch(e.target.value); loadScripts(e.target.value); }}
                />
              </div>
            </div>
          }
        />
        <Table
          headers={['Code', 'Name', 'Exchange', 'ISIN', 'Sector', 'Market Cap', 'Face Value', 'Actions']}
          rows={scripts.map(s => [
            <strong style={{ fontFamily: 'monospace' }}>{s.script_code}</strong>,
            s.script_name,
            <Badge color={s.exchange_code === 'NSE' ? 'blue' : 'orange'}>{s.exchange_code}</Badge>,
            <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{s.isin || '—'}</span>,
            s.sector || '—',
            s.market_cap_category ? <Badge color={s.market_cap_category === 'Large Cap' ? 'blue' : s.market_cap_category === 'Mid Cap' ? 'purple' : 'gray'}>{s.market_cap_category}</Badge> : '—',
            `₹${s.face_value}`,
            <Btn size="sm" variant="ghost" onClick={() => startEdit(s)}>Edit</Btn>
          ])}
          emptyMessage="No scripts found. Add your first script."
        />
      </Card>
    </div>
  );
}
